<template>
	<v-container fluid class="pa-3 pt-0">
		<v-card class="round-card elevation-1 planner-card overflow-hidden background-2">
		<!-- Toolbar -->
		<v-toolbar flat height="64" class="planner-toolbar">
			<v-btn icon @click="sidebarOpen = !sidebarOpen" class="ml-2"><v-icon>{{ sidebarOpen ? 'mdi-menu-open' : 'mdi-menu' }}</v-icon></v-btn>
			<v-spacer></v-spacer>
			<v-btn icon small @click="prev" class="ml-4"><v-icon>mdi-chevron-left</v-icon></v-btn>
			<v-btn text @click="setToday">Oggi</v-btn>
			<v-btn icon small @click="next"><v-icon>mdi-chevron-right</v-icon></v-btn>
			<v-toolbar-title class="subtitle-1 ml-2 planner-title">{{ title }}</v-toolbar-title>
			<v-chip v-if="activeFiltersCount > 0" small close @click:close="clearAllFilters" color="primary" text-color="white" class="ml-2">
				{{ activeFiltersCount }} filtri
			</v-chip>
			<v-spacer></v-spacer>
			<v-btn icon @click="printDailySchedule" title="Stampa programmazione"><v-icon>mdi-printer</v-icon></v-btn>
			<v-btn-toggle v-model="type" mandatory dense class="ml-2">
				<v-btn value="week" small text>Settimana</v-btn>
				<v-btn value="day" small text>Giorno</v-btn>
				<v-btn value="schedule" small text>Agenda</v-btn>
			</v-btn-toggle>
		</v-toolbar>
		<v-divider></v-divider>

		<v-row no-gutters class="planner-row">
			<!-- Sidebar Left: Ticket -->
			<v-col :cols="sidebarOpen ? 3 : 0" v-show="sidebarOpen" class="planner-sidebar planner-sidebar-left d-flex flex-column">
				<v-subheader class="px-3 flex-shrink-0"><v-icon small class="mr-1">mdi-ticket-outline</v-icon> Ticket ({{ filteredTickets.length }})</v-subheader>
				<div class="px-3 pb-2 flex-shrink-0 d-flex align-center">
					<v-text-field v-model="searchTickets" append-icon="mdi-magnify"
						placeholder="Cerca ticket..." outlined dense hide-details clearable single-line
						class="planner-search flex-grow-1"></v-text-field>
					<v-badge :value="selectedCompanies.length > 0" color="primary" dot overlap>
						<v-btn icon small class="ml-1"
							@click="filterPanelOpen = !filterPanelOpen" title="Filtri avanzati">
							<v-icon :color="selectedCompanies.length ? 'primary' : ''">filter_list</v-icon>
						</v-btn>
					</v-badge>
				</div>
				<v-expand-transition>
					<div v-show="filterPanelOpen" class="px-3 pb-2 flex-shrink-0">
						<AsyncAutocomplete
							v-model="selectedCompanies"
							async-url="async-companies"
							:multiple="true"
							:return-object="true"
							label="Filtra per azienda"
							item-text="text"
							item-value="value"
							:search-keys="['subtitle']"
							dense outlined hide-details clearable></AsyncAutocomplete>
					</div>
				</v-expand-transition>
				<div class="px-3 pb-2 flex-shrink-0">
					<v-chip v-for="st in ticketStatuses" :key="st" x-small class="mr-1 mb-1"
						:color="excludedStatuses.indexOf(st) === -1 ? ticketStatusFilterColor(st) : ''"
						:text-color="excludedStatuses.indexOf(st) === -1 ? 'white' : 'grey'"
						:outlined="excludedStatuses.indexOf(st) !== -1"
						:style="excludedStatuses.indexOf(st) !== -1 ? 'text-decoration: line-through; opacity: 0.4;' : 'font-weight: 600;'"
						@click="toggleStatusFilter(st)">
						{{ st }}
					</v-chip>
				</div>
				<div class="planner-sidebar-scroll flex-grow-1 overflow-y-auto">
					<div v-if="loadingTickets" class="d-flex justify-center align-center" style="min-height: 200px;">
						<v-progress-circular indeterminate color="primary" size="32" width="3"></v-progress-circular>
					</div>
					<v-list v-else dense class="pt-0">
						<v-list-item v-for="t in filteredTickets" :key="'t'+t.id" @click="openDialogFromTicket(t)" @mousedown="onTicketMouseDown($event, t)" class="planner-ticket-item px-3" :class="{'dragging': draggingTicket && draggingTicket.id === t.id}">
							<v-list-item-avatar size="24" class="mr-2">
								<v-avatar size="24" :color="ticketPriorityColor(t)">
									<v-icon x-small color="white">mdi-ticket-outline</v-icon>
								</v-avatar>
							</v-list-item-avatar>
							<v-list-item-content>
								<v-list-item-title class="caption font-weight-bold">#{{ t.number }}<span v-if="t.sectionalname">/{{ t.sectionalname }}</span> - {{ t.title }}</v-list-item-title>
								<v-list-item-subtitle v-if="t.company" class="caption">{{ t.company }}</v-list-item-subtitle>
							</v-list-item-content>
							<v-list-item-action>
								<v-chip x-small :color="ticketStatusColor(t.trouble_status_text)" :text-color="ticketStatusTextColor(t.trouble_status_text)" class="font-weight-medium">{{ t.trouble_status_text }}</v-chip>
							</v-list-item-action>
						</v-list-item>
						<v-list-item v-if="!ticketsList.length">
							<v-list-item-subtitle class="caption text--disabled">Nessun ticket</v-list-item-subtitle>
						</v-list-item>
					</v-list>
				</div>
			</v-col>

			<!-- Calendar / Agenda (centro) -->
			<v-col :cols="sidebarOpen ? 7 : 12" :class="'planner-view-' + type" class="planner-calendar-col">
				<!-- Agenda View -->
				<div v-if="type === 'schedule'" class="planner-agenda background-2" style="height: 100%; overflow-y: auto;">
					<!-- Toolbar agenda: espandi/collassa tutti -->
					<div v-if="agendaEvents.length" class="planner-agenda-toolbar d-flex align-center px-4 pt-3 pb-1">
						<v-spacer></v-spacer>
						<v-btn small text color="primary" @click="expandAllAgenda" :disabled="allAgendaExpanded">
							<v-icon small left>mdi-unfold-more-horizontal</v-icon>Espandi tutti
						</v-btn>
						<v-btn small text color="primary" @click="collapseAllAgenda" :disabled="!expandedAgendaIds.length">
							<v-icon small left>mdi-unfold-less-horizontal</v-icon>Collassa tutti
						</v-btn>
					</div>
					<div class="pa-4 pt-2">
						<div v-for="event in agendaEvents" :key="'a'+event.id"
							class="planner-agenda-item"
							:class="{'expanded': isAgendaExpanded(event.id)}"
							:style="'border-left-color:' + getEventColor(event) + ';'">
							<!-- Header: essential info (time, crew, name, vehicles). Click toggles expand. -->
							<div class="planner-agenda-header d-flex align-center" @click="toggleAgendaExpand(event.id)">
								<v-icon small class="mr-2">{{ isAgendaExpanded(event.id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
								<span class="planner-agenda-time mr-4">
									{{ formatTimeMs(event.started_at) }}–{{ formatTimeMs(event.ended_at) }}
								</span>
								<span v-if="event.users && event.users.length" class="body-2 font-weight-bold text-truncate mr-3 primary--text" style="max-width: 260px;">
									<v-icon x-small color="primary" class="mr-1">mdi-account-group</v-icon>{{ event.users.map(function(u){return u.text}).join(', ') }}
								</span>
								<span v-if="event.vehicles && event.vehicles.length" class="body-2 text-truncate mr-3 text--primary" style="max-width: 180px;">
									<v-icon x-small class="mr-1">mdi-car</v-icon>{{ event.vehicles.map(function(v){return v.text || v.license_plate}).join(', ') }}
								</span>
								<span class="caption text--secondary text-truncate" style="flex: 1;">{{ event.name }}</span>
							</div>
							<!-- Expanded panel: secondary details + quick "open" action -->
							<div v-if="isAgendaExpanded(event.id)" class="planner-agenda-details" @click.stop>
								<div v-if="event.entity_title" class="mb-1">
									<v-icon x-small class="mr-1">mdi-domain</v-icon>
									<strong>{{ event.entity_title }}</strong>
								</div>
								<div v-if="event.entity_address" class="mb-1 text--secondary">
									<v-icon x-small class="mr-1">mdi-map-marker</v-icon>{{ event.entity_address }}
								</div>
								<div v-if="event.description" class="mb-1 caption" style="white-space: pre-wrap;">{{ event.description }}</div>
								<!-- Elenco completo operatori con orario (evidenziato quando diverso dall'evento) -->
								<div v-if="event.users && event.users.length" class="mt-2 caption">
									<div class="text--secondary mb-1">
										<v-icon x-small class="mr-1">mdi-account-group</v-icon>Operatori
									</div>
									<div v-for="u in event.users" :key="'au'+event.id+'-'+u.value" class="ml-4">
										<strong>{{ u.text }}</strong>:
										<span :class="userHasCustomTime(event, u) ? 'orange--text text--darken-2 font-weight-bold' : 'text--secondary'">{{ formatUserTime(u) }}</span>
									</div>
								</div>
								<div class="mt-2">
									<v-btn x-small color="primary" text @click="showEventFromAgenda(event)">
										<v-icon x-small left>mdi-pencil</v-icon>Apri
									</v-btn>
								</div>
							</div>
						</div>
						<div v-if="!agendaEvents.length" class="text-center grey--text pa-8">
							<v-icon large color="grey lighten-2" class="mb-2">mdi-calendar-blank</v-icon>
							<div>Nessun evento pianificato</div>
						</div>
					</div>
				</div>
				<!-- Calendar Grid View -->
				<v-calendar v-else
					ref="calendar"
					v-model="focus"
					color="primary"
					locale="it"
					:type="calendarType"
					:events="filteredCalendarEvents"
					:event-color="getEventColor"
					:weekdays="[1, 2, 3, 4, 5, 6, 0]"
					:first-interval="7"
					:interval-count="17"
					:interval-height="calendarType === 'day' ? 72 : 48"
					:event-ripple="false"
					@mousedown:event="startDrag"
					@mousedown:time="startTime"
					@mousemove:time="mouseMove"
					@mouseup:time="endDrag"
					@mouseleave.native="cancelDrag"
					@click:event="showEvent"
					@click:date="viewDay"
					style="height: 100%;">

					<template v-slot:event="{ event, timed }">
						<div class="v-event-draggable"
							:class="[eventSizeClass(event), {'past-event': isPastEvent(event), 'v-event-draggable--dragging': dragEvent && dragEvent.id === event.id}]"
							:style="getEventTextColor(event)">
							<!-- Crew first: most important info for the planner -->
							<div class="event-crew" v-if="event.users && event.users.length">
								<v-icon x-small :style="getEventTextColor(event)">mdi-account-group</v-icon>
								<template v-for="(u, idx) in event.users"><span :key="'eu'+event.id+'-'+u.value">{{ u.text }}<span v-if="userHasCustomTime(event, u)" class="crew-custom-time"> ({{ formatUserTime(u) }})</span><span v-if="idx < event.users.length - 1">, </span></span></template>
							</div>
							<!-- Title + schedule (YelloCalendar pattern) -->
							<div :id="event.id" class="event-title">{{ event.name }}</div>
							<div :id="event.id" class="event-schedule" :class="eventDurationMinutes(event) > 30 ? 'd-block' : ''">
								{{ formatTimeMs(event.started_at) }} - {{ formatTimeMs(event.ended_at) }}
							</div>
							<!-- Secondary details: location + vehicles (hidden on short events and in week view).
							     Entity title (ticket name) is intenzionalmente omesso perché già presente in event.name. -->
							<div class="event-customer" v-if="showEventDetails(event) && event.entity_address">
								<v-icon x-small :style="getEventTextColor(event)">mdi-map-marker</v-icon> {{ event.entity_address }}
							</div>
							<div class="event-vehicles" v-if="showEventDetails(event) && event.vehicles && event.vehicles.length">
								<v-icon x-small :style="getEventTextColor(event)">mdi-car</v-icon>
								{{ event.vehicles.map(function(v){return v.text || v.license_plate}).join(', ') }}
							</div>
							<div v-if="timed" class="v-event-drag-bottom" @mousedown.stop="extendBottom(event)"></div>
						</div>
					</template>
				</v-calendar>
			</v-col>

			<!-- Sidebar Right: Operatori + Veicoli (2 sezioni con header fissi + scroll per lista) -->
			<v-col :cols="sidebarOpen ? 2 : 0" v-show="sidebarOpen" class="planner-sidebar planner-sidebar-right d-flex flex-column">
				<!-- Operatori -->
				<div class="planner-side-section d-flex flex-column">
					<v-subheader class="px-3 flex-shrink-0">
						<v-icon small class="mr-1">mdi-account-group</v-icon> Operatori ({{ usersWithCount.length }})
						<v-spacer></v-spacer>
						<v-btn x-small text v-if="selectedUsers.length" @click="selectedUsers = []" color="primary">Tutti</v-btn>
					</v-subheader>
					<div class="px-3 pb-2 flex-shrink-0">
						<v-text-field v-model="searchUsers" append-icon="mdi-magnify"
							placeholder="Cerca operatore..." outlined dense hide-details clearable single-line
							class="planner-search"></v-text-field>
					</div>
					<div class="planner-side-scroll flex-grow-1 overflow-y-auto">
						<v-list dense class="pt-0">
							<v-list-item v-for="u in usersWithCount" :key="'u'+u.value"
								@click="toggleUserFilter(u.value)" class="px-3 planner-filter-item"
								:class="{ 'planner-filter-active': selectedUsers.indexOf(u.value) !== -1 }">
								<v-list-item-action class="mr-1 my-0">
									<v-simple-checkbox hide-details :value="selectedUsers.indexOf(u.value) !== -1" @input="toggleUserFilter(u.value)" color="primary" dense></v-simple-checkbox>
								</v-list-item-action>
								<v-list-item-avatar size="24" class="mr-1">
									<v-avatar size="24" :color="u.color || 'grey lighten-1'">
										<span class="white--text" style="font-size:10px;">{{ u.text ? u.text.charAt(0) : '?' }}</span>
									</v-avatar>
								</v-list-item-avatar>
								<v-list-item-content>
									<v-list-item-title class="caption">{{ u.text }}</v-list-item-title>
								</v-list-item-content>
								<v-list-item-action class="my-0">
									<v-chip x-small :color="u.eventCount > 0 ? 'blue lighten-4' : 'grey lighten-3'" :text-color="u.eventCount > 0 ? 'blue darken-3' : 'grey'">
										{{ u.eventCount }}
									</v-chip>
								</v-list-item-action>
							</v-list-item>
						</v-list>
					</div>
				</div>
				<v-divider></v-divider>
				<!-- Veicoli -->
				<div class="planner-side-section d-flex flex-column">
					<v-subheader class="px-3 flex-shrink-0">
						<v-icon small class="mr-1">mdi-car</v-icon> Veicoli ({{ vehiclesWithCount.length }})
						<v-spacer></v-spacer>
						<v-btn x-small text v-if="selectedVehicles.length" @click="selectedVehicles = []" color="primary">Tutti</v-btn>
					</v-subheader>
					<div class="px-3 pb-2 flex-shrink-0">
						<v-text-field v-model="searchVehicles" append-icon="mdi-magnify"
							placeholder="Cerca veicolo..." outlined dense hide-details clearable single-line
							class="planner-search"></v-text-field>
					</div>
					<div class="planner-side-scroll flex-grow-1 overflow-y-auto">
						<v-list dense class="pt-0">
							<v-list-item v-for="v in vehiclesWithCount" :key="'v'+v.value"
								@click="toggleVehicleFilter(v.value)" class="px-3 planner-filter-item"
								:class="{ 'planner-filter-active': selectedVehicles.indexOf(v.value) !== -1 }">
								<v-list-item-action class="mr-1 my-0">
									<v-simple-checkbox :value="selectedVehicles.indexOf(v.value) !== -1" @input="toggleVehicleFilter(v.value)" color="primary" dense></v-simple-checkbox>
								</v-list-item-action>
								<v-list-item-content>
									<v-list-item-title class="caption font-weight-bold">{{ v.text }}</v-list-item-title>
								</v-list-item-content>
								<v-list-item-action v-if="v.next_expiration" class="my-0 mr-1">
									<v-icon small :color="vehicleExpirationColor(v)">
										{{ isExpiringSoon(v) ? 'mdi-alert' : 'mdi-check-circle' }}
									</v-icon>
								</v-list-item-action>
								<v-list-item-action class="my-0">
									<v-chip x-small :color="v.eventCount > 0 ? 'blue lighten-4' : 'grey lighten-3'" :text-color="v.eventCount > 0 ? 'blue darken-3' : 'grey'">
										{{ v.eventCount }}
									</v-chip>
								</v-list-item-action>
							</v-list-item>
						</v-list>
					</div>
				</div>
			</v-col>
		</v-row>
		</v-card>

		<!-- Event Dialog -->
		<v-dialog v-model="dialogOpen" max-width="650" persistent>
			<v-card class="background-2 round-card">
				<v-form ref="form" v-model="formValid" @submit.prevent="saveEvent">
				<!-- Titolo prominente -->
				<v-card-title class="pa-3 pb-0">
					<v-text-field v-model="form.name" placeholder="Aggiungi titolo"
						solo flat hide-details class="planner-title-input text-h6"
						:rules="[v => !!v && !!v.trim() || 'Titolo richiesto']"></v-text-field>
				</v-card-title>

				<v-divider></v-divider>

				<v-card-text class="pa-6">
					<!-- Ticket collegato -->
					<div class="d-flex align-center mb-3">
						<v-icon class="mr-3" color="grey">mdi-ticket-outline</v-icon>
						<v-autocomplete
							v-model="form.ticket"
							:items="ticketsList"
							item-text="title"
							item-value="id"
							label="Ticket collegato"
							outlined dense hide-details clearable
							return-object
							@change="onTicketSelected"
							style="flex: 1;">
							<template v-slot:item="{ item }">
								<v-list-item-content>
									<v-list-item-title>#{{ item.number }}<span v-if="item.sectionalname">/{{ item.sectionalname }}</span> - {{ item.title }}</v-list-item-title>
									<v-list-item-subtitle v-if="item.company">{{ item.company }}</v-list-item-subtitle>
								</v-list-item-content>
							</template>
						</v-autocomplete>
					</div>

					<!-- Fasce orarie (configurabili da /settingstickets/planner) -->
					<div v-if="timeSlots.length" class="d-flex align-center mb-3">
						<v-icon class="mr-3" color="grey">mdi-clock-fast</v-icon>
						<v-btn-toggle :value="selectedSlotId" @change="applyTimeSlot" dense :mandatory="false" color="primary">
							<v-btn v-for="slot in timeSlots" :key="'slot'+slot.id" :value="slot.id" small>
								{{ slot.name }}
								<span class="caption grey--text ml-1">{{ slot.time_from }}–{{ slot.time_to }}</span>
							</v-btn>
						</v-btn-toggle>
					</div>

					<!-- Data e Orario su una riga -->
					<div class="d-flex align-start mb-3">
						<v-icon class="mr-3 mt-2" color="grey">mdi-clock-outline</v-icon>
						<div class="mr-2" style="max-width: 170px;">
							<gmh-date-picker v-model="form.date" hide-details editable :clearable="false"
								:nudge-right="0"
								:rules="[v => !!v || 'Data richiesta']"></gmh-date-picker>
						</div>
						<v-text-field v-model="form.startTime" type="time" outlined dense hide-details
							class="mr-1" style="max-width: 110px;"
							:rules="[v => !!v || 'Orario richiesto']"></v-text-field>
						<span class="mx-1 mt-2 grey--text">–</span>
						<v-text-field v-model="form.endTime" type="time" outlined dense hide-details
							style="max-width: 110px;"
							:rules="endTimeRules"></v-text-field>
					</div>

					<!-- Operatori -->
					<div class="d-flex align-start mb-3">
						<v-icon class="mr-3 mt-2" color="grey">mdi-account-group</v-icon>
						<div style="flex: 1;">
							<v-autocomplete
								v-model="form.users"
								:items="users"
								item-text="text"
								item-value="value"
								label="Operatori (squadra)"
								outlined dense multiple chips small-chips deletable-chips hide-details
								return-object></v-autocomplete>
							<!-- Orari custom per operatore -->
							<div v-if="crewWithTimes.length" class="mt-2">
								<div v-for="u in crewWithTimes" :key="'ut'+u.value" class="d-flex align-center mb-1">
									<span class="caption grey--text" style="min-width: 110px;">{{ u.text }}</span>
									<v-text-field
										:value="u.start"
										@input="setCrewTime(u.value, 'start', $event)"
										type="time" outlined dense hide-details
										class="mx-1" style="max-width: 110px;"></v-text-field>
									<span class="mx-1 grey--text caption">–</span>
									<v-text-field
										:value="u.end"
										@input="setCrewTime(u.value, 'end', $event)"
										type="time" outlined dense hide-details
										class="mx-1" style="max-width: 110px;"></v-text-field>
								</div>
								<div class="caption grey--text text--lighten-1">Gli orari si allineano all'evento. Modificali per impostare orari personali.</div>
							</div>
						</div>
					</div>

					<!-- Veicoli -->
					<div class="d-flex align-start mb-3">
						<v-icon class="mr-3 mt-2" color="grey">mdi-car</v-icon>
						<v-autocomplete
							v-model="form.vehicles"
							:items="vehicles"
							item-text="text"
							item-value="value"
							label="Veicoli"
							outlined dense multiple chips small-chips deletable-chips hide-details
							return-object
							style="flex: 1;"></v-autocomplete>
					</div>

					<!-- Note -->
					<div class="d-flex align-start mb-2">
						<v-icon class="mr-3 mt-2" color="grey">mdi-text</v-icon>
						<v-textarea v-model="form.description" label="Note"
							outlined dense hide-details rows="2" auto-grow style="flex: 1;"></v-textarea>
					</div>
				</v-card-text>

				<v-divider></v-divider>
				<v-card-actions class="pa-3">
					<!-- Two-click confirm: first click flips confirmDelete=true and changes label to "Conferma".
					     Second click within 4s actually triggers the delete. Auto-resets via watcher in deleteEvent. -->
					<v-btn v-if="dialogEdit" text :color="confirmDelete ? 'red darken-2' : 'red'"
						:loading="deleting" @click="deleteEvent">
						<v-icon small left>{{ confirmDelete ? 'mdi-alert' : 'mdi-delete' }}</v-icon>
						{{ confirmDelete ? 'Conferma eliminazione' : 'Elimina' }}
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn text color="info" @click="closeDialog" :disabled="saving || deleting">Annulla</v-btn>
					<v-btn color="primary" depressed type="submit" :loading="saving" :disabled="deleting">{{ dialogEdit ? 'Salva' : 'Crea' }}</v-btn>
				</v-card-actions>
				</v-form>
			</v-card>
		</v-dialog>

		<!-- Undo Snackbar -->
		<v-snackbar v-model="undoSnackbar.show" :timeout="5000" bottom>
			{{ undoSnackbar.text }}
			<template v-slot:action="{ attrs }">
				<v-btn text color="primary" v-bind="attrs" @click="if(undoSnackbar.undo) undoSnackbar.undo(); undoSnackbar.show = false">Annulla</v-btn>
			</template>
		</v-snackbar>
	</v-container>
</template>

<script>
import AsyncAutocomplete from '@/components/common/AsyncAutocomplete.vue'

export default {
	name: 'PlannerCalendar',

	components: { AsyncAutocomplete },

	data() {
		return {
			// Sidebar data
			tickets: [],
			users: [],
			vehicles: [],
			calendars: [],
			// Calendar
			events: [],
			type: 'day',
			focus: '',
			title: '',
			loading: false,
			loadingTickets: false,
			excludedStatuses: ['Chiuso'],
			selectedUsers: [],
			selectedVehicles: [],
			selectedCompanies: [],  // array oggetti company per filtro ticket
			filterPanelOpen: false,
			searchTickets: '',
			searchUsers: '',
			searchVehicles: '',
			// Drag state
			dragEvent: null,
			dragTime: null,
			createEvent: null,
			createStart: null,
			extendOriginal: null,
			dummyId: 0,
			// Dialog
			dialogOpen: false,
			dialogEdit: false,
			saving: false,
			form: this.emptyForm(),
			// Sidebar drag
			draggingTicket: null,
			// Undo snackbar
			undoSnackbar: { show: false, text: '', undo: null },
			// Sidebar state
			sidebarOpen: true,
			// Agenda view expand state: set di id espansi (più di uno per volta)
			expandedAgendaIds: [],
			// Form state
			formValid: true,
			deleting: false,
			// Two-click confirm for delete: first click sets this to true, second triggers actual delete.
			// Auto-resets after 4s if no second click.
			confirmDelete: false,
			confirmDeleteTimer: null,
			// Time slots (configurable da /settingstickets/planner — settings(model='planners', key='time_slots'))
			timeSlots: [],
			selectedSlotId: null,
			applyingSlot: false,
		}
	},

	computed: {
		ticketsList() {
			return Array.isArray(this.tickets) ? this.tickets : []
		},
		ticketStatuses() {
			var statuses = []
			this.ticketsList.forEach(function(t) {
				if (t.trouble_status_text && statuses.indexOf(t.trouble_status_text) === -1) {
					statuses.push(t.trouble_status_text)
				}
			})
			// Gli stati esclusi restano visibili come chip: con not_closed server-side i ticket
			// 'Chiuso' non arrivano più nella pagina — senza questa aggiunta il chip sparirebbe
			// e non sarebbe più riattivabile.
			this.excludedStatuses.forEach(function(st) {
				if (statuses.indexOf(st) === -1) statuses.push(st)
			})
			return statuses
		},
		filteredTickets() {
			// Ricerca testuale: server-side (param filter in loadTickets). Qui resta solo
			// l'esclusione per stato, che opera sulla pagina caricata.
			var self = this
			return this.ticketsList.filter(function(t) {
				if (self.excludedStatuses.length && t.trouble_status_text && self.excludedStatuses.indexOf(t.trouble_status_text) !== -1) return false
				return true
			})
		},
		// Range di date visibile (per contare gli eventi nei counter operatori/veicoli).
		// Reattivo su this.focus e this.type (non su $refs, che non aggiornerebbe i computed).
		// - 'day'/'schedule' → singolo giorno
		// - 'week' → intervallo lun-dom della settimana del focus
		// IMPORTANTE: fallback in ORA LOCALE perché gli started_at degli eventi vengono
		// convertiti con new Date(ms).getFullYear/Month/Date (local) in usersWithCount;
		// toISOString() userebbe UTC e di notte introdurrebbe uno sfasamento di un giorno.
		eventRange() {
			function p(n) { return n < 10 ? '0' + n : '' + n }
			function fmt(dt) { return dt.getFullYear() + '-' + p(dt.getMonth()+1) + '-' + p(dt.getDate()) }
			var d = this.focus || this.todayLocal()
			if (this.type === 'week') {
				var date = new Date(d + 'T12:00:00')
				var day = date.getDay() || 7  // Mon=1..Sun=7
				var monday = new Date(date); monday.setDate(date.getDate() - (day - 1))
				var sunday = new Date(monday); sunday.setDate(monday.getDate() + 6)
				return { from: fmt(monday), to: fmt(sunday) }
			}
			return { from: d, to: d }
		},
		usersWithCount() {
			var range = this.eventRange
			var parentEvents = this.events.filter(function(e) { return !e.event_id })
			var q = (this.searchUsers || '').toLowerCase().trim()
			function p(n) { return n < 10 ? '0' + n : '' + n }
			function fmtMs(ms) { var dt = new Date(ms); return dt.getFullYear() + '-' + p(dt.getMonth()+1) + '-' + p(dt.getDate()) }
			return this.users.filter(function(u) {
				if (!q) return true
				return (u.text || '').toLowerCase().indexOf(q) !== -1
			}).map(function(u) {
				var count = parentEvents.filter(function(e) {
					var eDate = fmtMs(e.started_at)
					return eDate >= range.from && eDate <= range.to && e.users && e.users.some(function(eu) { return eu.value === u.value })
				}).length
				return Object.assign({}, u, { eventCount: count })
			})
		},
		vehiclesWithCount() {
			var range = this.eventRange
			var parentEvents = this.events.filter(function(e) { return !e.event_id })
			var q = (this.searchVehicles || '').toLowerCase().trim()
			function p(n) { return n < 10 ? '0' + n : '' + n }
			function fmtMs(ms) { var dt = new Date(ms); return dt.getFullYear() + '-' + p(dt.getMonth()+1) + '-' + p(dt.getDate()) }
			return this.vehicles.filter(function(v) {
				if (!q) return true
				return (v.text || '').toLowerCase().indexOf(q) !== -1
			}).map(function(v) {
				var count = parentEvents.filter(function(e) {
					var eDate = fmtMs(e.started_at)
					return eDate >= range.from && eDate <= range.to && e.vehicles && e.vehicles.some(function(ev) { return (ev.value || ev.id) === v.value })
				}).length
				return Object.assign({}, v, { eventCount: count })
			})
		},
		calendarEvents() {
			// Filter out shared copies (event_id != null) — show only parent events
			return this.events.filter(function(e) {
				return !e.event_id
			}).map(function(e) {
				// v-calendar needs start/end as "YYYY-MM-DD HH:mm" strings
				if (typeof e.start === 'string') return e
				// Convert ms timestamps to strings for display
				var s = new Date(e.started_at)
				var d = new Date(e.ended_at)
				function p(n) { return n < 10 ? '0' + n : '' + n }
				// Color policy: ticket priority drives the event color.
				// Priority codes come from general_types (key=trouble_priority): a/b/c/d.
				// a=Molto Alta, b=Alta, c=Media, d=Bassa. Fallback when missing: neutral blue-grey.
				var eventColor = e.entity_priority === 'a' ? '#D32F2F'
					: e.entity_priority === 'b' ? '#F57C00'
					: e.entity_priority === 'c' ? '#FBC02D'
					: e.entity_priority === 'd' ? '#43A047'
					: '#546E7A'
				return Object.assign({}, e, {
					start: s.getFullYear() + '-' + p(s.getMonth()+1) + '-' + p(s.getDate()) + ' ' + p(s.getHours()) + ':' + p(s.getMinutes()),
					end: d.getFullYear() + '-' + p(d.getMonth()+1) + '-' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes()),
					color: eventColor,
					timed: !e.allday,
				})
			})
		},
		filteredCalendarEvents() {
			var events = this.calendarEvents
			var self = this
			if (this.selectedUsers.length) {
				events = events.filter(function(e) {
					return e.users && e.users.some(function(u) { return self.selectedUsers.indexOf(u.value) !== -1 })
				})
			}
			if (this.selectedVehicles.length) {
				events = events.filter(function(e) {
					return e.vehicles && e.vehicles.some(function(v) { return self.selectedVehicles.indexOf(v.value || v.id) !== -1 })
				})
			}
			return events
		},
		activeFiltersCount() {
			return this.selectedUsers.length + this.selectedVehicles.length
		},
		calendarType() {
			// v-calendar only supports 'week' and 'day' — default to 'day' for schedule
			return this.type === 'schedule' ? 'day' : this.type
		},
		agendaEvents() {
			var events = this.filteredCalendarEvents.slice()
			events.sort(function(a, b) { return a.started_at - b.started_at })
			return events
		},
		allAgendaExpanded() {
			return this.agendaEvents.length > 0 && this.expandedAgendaIds.length === this.agendaEvents.length
		},
		// End time must be present AND strictly after start time. Validates only when both are set.
		endTimeRules() {
			var self = this
			return [
				function(v) { return !!v || 'Orario richiesto' },
				function(v) {
					if (!v || !self.form.startTime) return true
					return v > self.form.startTime || 'Fine deve essere dopo l\'inizio'
				},
			]
		},
		// Always returns one entry per selected user with a guaranteed start/end.
		// Reactive to form.users, form.customTimes (deep), form.startTime, form.endTime.
		crewWithTimes() {
			var self = this
			return (this.form.users || []).map(function(u) {
				if (!u || u.value == null) return null
				var ct = self.form.customTimes[u.value]
				var useCustom = ct && ct.touched && ct.start && ct.end
				return {
					value: u.value,
					text: u.text,
					start: useCustom ? ct.start : self.form.startTime,
					end: useCustom ? ct.end : self.form.endTime,
				}
			}).filter(function(x) { return !!x })
		},
	},

	mounted() {
		this.loadSidebar()
		this.loadTimeSlots()
		this.$nextTick(function() {
			this.updateTitle()
			this.loadEvents()
		})
		document.addEventListener('mouseup', this.onGlobalMouseUp)
		this.$store.commit('endProgress')
	},

	// NB: UNICO blocco watch del componente. Una seconda chiave `watch:` nell'options object
	// sovrascriverebbe silenziosamente questa (è già successo: i watcher del form sono rimasti
	// dead code finché il blocco duplicato in fondo al file non è stato fuso qui).
	watch: {
		type() {
			this.$nextTick(function() { this.updateTitle(); this.loadEvents() }.bind(this))
		},
		selectedCompanies() {
			this.loadTickets()
		},
		searchTickets() {
			// Ricerca server-side (param filter): debounce per non chiamare l'API a ogni tasto.
			clearTimeout(this._searchTicketsTimer)
			this._searchTicketsTimer = setTimeout(this.loadTickets, 350)
		},
		'form.users': function(newUsers) {
			if (!Array.isArray(newUsers)) return
			var self = this
			// Add customTimes for new users, pre-filled with main event times
			newUsers.forEach(function(u) {
				if (u && u.value != null && !self.form.customTimes[u.value]) {
					self.$set(self.form.customTimes, u.value, {
						start: self.form.startTime,
						end: self.form.endTime,
						touched: false,
					})
				}
			})
			// Cleanup customTimes for removed users
			var currentIds = newUsers.map(function(u) { return u && u.value }).filter(function(v) { return v != null })
			Object.keys(this.form.customTimes).forEach(function(key) {
				var numKey = parseInt(key, 10)
				if (currentIds.indexOf(numKey) === -1 && currentIds.indexOf(key) === -1) {
					self.$delete(self.form.customTimes, key)
				}
			})
		},
		'form.startTime': function() {
			if (!this.applyingSlot) this.selectedSlotId = null
			this.propagateEventTimeToCrew()
		},
		'form.endTime': function() {
			if (!this.applyingSlot) this.selectedSlotId = null
			this.propagateEventTimeToCrew()
		},
		dialogOpen: function(isOpen) {
			// Quando il dialog si apre/chiude, deseleziona eventuale toggle fascia (start da pulito).
			// In edit tentiamo poi di matchare la fascia esistente.
			if (!isOpen) {
				this.selectedSlotId = null
			} else {
				this.selectedSlotId = null
				var self = this
				this.$nextTick(function() {
					var match = self.timeSlots.find(function(s) {
						return s.time_from === self.form.startTime && s.time_to === self.form.endTime
					})
					if (match) self.selectedSlotId = match.id
				})
			}
		},
	},

	beforeDestroy() {
		document.removeEventListener('mouseup', this.onGlobalMouseUp)
		if (this.confirmDeleteTimer) clearTimeout(this.confirmDeleteTimer)
		if (this._searchTicketsTimer) clearTimeout(this._searchTicketsTimer)
	},

	methods: {
		emptyForm() {
			return { name: '', ticket: null, date: '', startTime: '09:00', endTime: '10:00', users: [], customTimes: {}, vehicles: [], description: '' }
		},

		// Carica le fasce orarie predefinite dalla tabella settings (model='planners', key='time_slots').
		// Configurabili da /settingstickets/planner.
		loadTimeSlots() {
			var self = this
			this.axios.get('settings/planners/time_slots').then(function(response) {
				var rows = (response.data && response.data.settings) || []
				var row = rows.find(function(r) { return r.key === 'time_slots' })
				self.timeSlots = (row && Array.isArray(row.value)) ? row.value.slice().sort(function(a, b) { return (a.order || 0) - (b.order || 0) }) : []
			}, function() {
				self.timeSlots = []
			})
		},

		// Applica una fascia oraria: popola startTime/endTime e propaga agli operatori non touched.
		applyTimeSlot(slotId) {
			if (slotId == null) {
				this.selectedSlotId = null
				return
			}
			var slot = this.timeSlots.find(function(s) { return s.id === slotId })
			if (!slot) return
			var self = this
			this.applyingSlot = true
			this.selectedSlotId = slotId
			this.form.startTime = slot.time_from
			this.form.endTime = slot.time_to
			// Reset touched per tutti i crew members — la fascia è scelta esplicita dall'utente
			Object.keys(this.form.customTimes).forEach(function(key) {
				self.$set(self.form.customTimes, key, {
					start: slot.time_from,
					end: slot.time_to,
					touched: false,
				})
			})
			this.$nextTick(function() { self.applyingSlot = false })
		},

		// Returns the current user as an option object ready for form.users (or null if not available in the sidebar list).
		getCurrentUser() {
			var profile = this.$store && this.$store.getters ? this.$store.getters.getProfile : null
			if (!profile || !profile.id) return null
			var found = (this.users || []).find(function(u) { return u.value === profile.id })
			return found ? { value: found.value, text: found.text, subtitle: found.subtitle } : null
		},

		// Synchronously initialize form.customTimes for the given users with current event times.
		// Call this right after setting form.users to ensure the per-operator time inputs appear on first render.
		initCustomTimesForUsers(users) {
			var self = this
			var times = {}
			;(users || []).forEach(function(u) {
				if (u && u.value != null) {
					times[u.value] = { start: self.form.startTime, end: self.form.endTime, touched: false }
				}
			})
			this.form.customTimes = times
		},

		// Writes a per-operator time value and flags the entry as touched (so it survives propagate).
		setCrewTime(userId, field, value) {
			var existing = this.form.customTimes[userId] || { start: this.form.startTime, end: this.form.endTime, touched: false }
			var updated = {
				start: existing.start,
				end: existing.end,
				touched: true,
			}
			updated[field] = value
			this.$set(this.form.customTimes, userId, updated)
		},

		// Propagates main event start/end times to crew members that have not edited their personal times.
		propagateEventTimeToCrew() {
			var self = this
			Object.keys(this.form.customTimes).forEach(function(key) {
				var ct = self.form.customTimes[key]
				if (ct && !ct.touched) {
					self.$set(ct, 'start', self.form.startTime)
					self.$set(ct, 'end', self.form.endTime)
				}
			})
		},

		// --- Data Loading ---
		loadSidebar() {
			this.loading = true
			// Planner dedicated endpoint: backend sempre include self nella crew (dispatcher può essere parte del team).
			this.axios.get('planners-info').then(function(res) {
				var fields = (res.data && res.data.fields) ? res.data.fields : {}
				this.calendars = fields.calendars || []
				this.users = fields.users || []
				this.vehicles = fields.vehicles || []
				this.loading = false
			}.bind(this)).catch(function() { this.loading = false }.bind(this))
			this.loadTickets()
		},
		loadTickets() {
			this.loadingTickets = true
			var subFilters = {}
			if (this.selectedCompanies.length) {
				subFilters.companies = this.selectedCompanies.map(function(c) { return c.value })
			}
			// 'Chiuso' escluso → subFilter server not_closed (whereIn sugli status aperti):
			// con la paginazione il filtro client-side non basta, opererebbe solo sulla pagina caricata.
			if (this.excludedStatuses.indexOf('Chiuso') !== -1) {
				subFilters.not_closed = true
			}
			// Paginazione: il backend (BaseModel::queryFilters) legge SOLO page/per_page top-level;
			// senza per_page risponde con TUTTI i record in una pagina → freeze su tenant grandi.
			var payload = { page: 1, per_page: 50, subFilters: subFilters }
			var q = (this.searchTickets || '').trim()
			if (q) payload.filter = q
			this.axios.post('troubles-filters', payload).then(function(res) {
				var d = res.data
				if (d && d.data && Array.isArray(d.data)) this.tickets = d.data
				else if (d && d.data && d.data.data) this.tickets = d.data.data
				else this.tickets = []
				this.loadingTickets = false
			}.bind(this)).catch(function() { this.loadingTickets = false }.bind(this))
		},

		// Data di oggi in formato "YYYY-MM-DD" in ORA LOCALE (non UTC).
		// Necessario perché gli started_at degli eventi vengono convertiti con
		// new Date(ms).getFullYear/Month/Date (local) nei filtri sul range di date.
		// Usare toISOString() darebbe la data UTC e di notte sfaserebbe di un giorno.
		todayLocal() {
			var d = new Date()
			function p(n) { return n < 10 ? '0' + n : '' + n }
			return d.getFullYear() + '-' + p(d.getMonth()+1) + '-' + p(d.getDate())
		},
		loadEvents() {
			var from, to
			if (this.type === 'schedule') {
				var focusDate = this.focus || this.todayLocal()
				from = focusDate + ' 00:00'
				to = focusDate + ' 23:59'
			} else {
				var cal = this.$refs.calendar
				if (!cal || !cal.lastStart) return
				from = cal.lastStart.date + ' 00:00'
				to = cal.lastEnd.date + ' 23:59'
			}
			this.axios.post('planners-filters', {
				subFilters: { from: from, to: to }
			}).then(function(res) {
				var data = (res.data && res.data.data) ? res.data.data : []
				this.events = Array.isArray(data) ? data : []
			}.bind(this)).catch(function() {})
		},

		// --- Navigation ---
		updateTitle() {
			if (this.type === 'schedule') {
				var d = this.focus || this.todayLocal()
				var dt = new Date(d + 'T12:00:00')
				this.title = dt.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
			} else {
				var cal = this.$refs.calendar
				this.title = cal ? cal.title : ''
			}
		},
		prev() {
			if (this.type === 'schedule') {
				var d = new Date((this.focus || this.todayLocal()) + 'T12:00:00')
				d.setDate(d.getDate() - 1)
				function p(n) { return n < 10 ? '0' + n : '' + n }
				this.focus = d.getFullYear() + '-' + p(d.getMonth()+1) + '-' + p(d.getDate())
				this.$nextTick(function() { this.updateTitle(); this.loadEvents() }.bind(this))
			} else {
				this.$refs.calendar.prev()
				this.$nextTick(function() { this.updateTitle(); this.loadEvents() }.bind(this))
			}
		},
		next() {
			if (this.type === 'schedule') {
				var d = new Date((this.focus || this.todayLocal()) + 'T12:00:00')
				d.setDate(d.getDate() + 1)
				function p(n) { return n < 10 ? '0' + n : '' + n }
				this.focus = d.getFullYear() + '-' + p(d.getMonth()+1) + '-' + p(d.getDate())
				this.$nextTick(function() { this.updateTitle(); this.loadEvents() }.bind(this))
			} else {
				this.$refs.calendar.next()
				this.$nextTick(function() { this.updateTitle(); this.loadEvents() }.bind(this))
			}
		},
		setToday() {
			this.focus = ''
			this.$nextTick(function() { this.updateTitle(); this.loadEvents() }.bind(this))
		},
		printDailySchedule() {
			var cal = this.$refs.calendar
			var date = cal && cal.value ? cal.value : this.todayLocal()
			this.axios.get('planners/daily-print?date=' + date, { responseType: 'blob' }).then(function(res) {
				var url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
				window.open(url, '_blank')
			}.bind(this)).catch(function() {
				this.$store.commit('setSnackbar', { timeout: 3000, text: 'Errore generazione PDF', value: true, color: 'error' })
			}.bind(this))
		},
		viewDay(ev) {
			this.focus = ev.date
			this.type = 'day'
			this.$nextTick(function() { this.updateTitle(); this.loadEvents() }.bind(this))
		},

		onTicketMouseDown(ev, ticket) {
			if (ev.button !== 0) return
			this.draggingTicket = ticket
		},
		onGlobalMouseUp() {
			this.draggingTicket = null
		},

		// --- Drag & Drop (from YelloCalendar pattern) ---
		toTime(tms) {
			return new Date(tms.year, tms.month - 1, tms.day, tms.hour, tms.minute).getTime()
		},
		roundTime(time, down) {
			if (down === undefined) down = true
			var roundTo = 15 * 60 * 1000
			return down ? time - time % roundTo : time + (roundTo - (time % roundTo))
		},

		startDrag(ev) {
			if (ev.nativeEvent && ev.nativeEvent.button !== 0) return
			if (ev.event && ev.timed) {
				this.dragEvent = ev.event
				this._dragOriginalStart = ev.event.started_at
				this._dragOriginalEnd = ev.event.ended_at
				this.dragTime = null
				this.extendOriginal = null
			}
		},

		startTime(tms, nativeEvent) {
			if (nativeEvent && nativeEvent.button !== 0) return
			var mouse = this.toTime(tms)

			// Drop ticket from sidebar
			if (this.draggingTicket) {
				var ticket = this.draggingTicket
				this.draggingTicket = null
				this.form = this.emptyForm()
				this.form.ticket = ticket
				this.form.name = '#' + ticket.number + (ticket.sectionalname ? '/' + ticket.sectionalname : '') + ' - ' + ticket.title
				var d = new Date(mouse)
				function p(n) { return n < 10 ? '0' + n : '' + n }
				this.form.date = d.getFullYear() + '-' + p(d.getMonth()+1) + '-' + p(d.getDate())
				this.form.startTime = p(d.getHours()) + ':' + p(d.getMinutes())
				var endMs = mouse + 3600000
				var e = new Date(endMs)
				this.form.endTime = p(e.getHours()) + ':' + p(e.getMinutes())
				var me = this.getCurrentUser()
				if (me) {
					this.form.users = [me]
					this.initCustomTimesForUsers([me])
				}
				this.dialogEdit = false
				this.dialogOpen = true
				return
			}

			if (this.dragEvent && this.dragTime === null) {
				this.dragTime = mouse - this.dragEvent.started_at
			} else {
				this.createStart = this.roundTime(mouse)
				this.createEvent = {
					name: 'Nuovo evento',
					started_at: this.createStart,
					ended_at: this.createStart + 3600000,
					timed: true,
					allday: false,
					color: '#1976D2',
					id: --this.dummyId,
				}
				this.events.push(this.createEvent)
			}
		},

		_formatEventTime(ms) {
			var d = new Date(ms)
			function p(n) { return n < 10 ? '0' + n : '' + n }
			return d.getFullYear() + '-' + p(d.getMonth()+1) + '-' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes())
		},
		_syncEventToSource(event) {
			// Update the source event in this.events to trigger reactivity
			for (var i = 0; i < this.events.length; i++) {
				if (this.events[i].id === event.id) {
					this.$set(this.events[i], 'started_at', event.started_at)
					this.$set(this.events[i], 'ended_at', event.ended_at)
					break
				}
			}
		},
		mouseMove(tms) {
			var mouse = this.toTime(tms)
			if (this.dragEvent && this.dragTime !== null) {
				var duration = this.dragEvent.ended_at - this.dragEvent.started_at
				var newStart = this.roundTime(mouse - this.dragTime)
				this.dragEvent.started_at = newStart
				this.dragEvent.ended_at = newStart + duration
				// Update v-calendar rendering props
				this.dragEvent.start = this._formatEventTime(newStart)
				this.dragEvent.end = this._formatEventTime(newStart + duration)
				// Sync to source for reactivity
				this._syncEventToSource(this.dragEvent)
			} else if (this.createEvent && this.createStart !== null) {
				var mouseRounded = this.roundTime(mouse, false)
				var newStartC = Math.min(mouseRounded, this.createStart)
				var newEndC = Math.max(mouseRounded, this.createStart)
				this.createEvent.started_at = newStartC
				this.createEvent.ended_at = newEndC
				this.createEvent.start = this._formatEventTime(newStartC)
				this.createEvent.end = this._formatEventTime(newEndC)
			}
		},

		endDrag() {
			// Existing event moved
			if (this.dragEvent && this.dragEvent.id > 0 && this.dragTime !== null) {
				this.quickUpdateEvent(this.dragEvent)
			}
			// New event created by drag
			else if (this.createEvent && this.createEvent.id <= 0) {
				this.openDialogFromDrag(this.createEvent)
			}
			// Existing event resized
			else if (this.createEvent && this.createEvent.id > 0) {
				this.quickUpdateEvent(this.createEvent)
			}
			// Reset dragTime/createStart immediately — dragEvent is cleared inside the PUT response
			// (in quickUpdateEvent) so the v-calendar keeps the dragged event anchored to its new
			// coordinates until the server acks. Otherwise week view re-renders from stale state
			// during the async window and the event appears to "snap back".
			this.dragTime = null
			this.createStart = null
		},

		extendBottom(event) {
			this.createEvent = event
			this.createStart = event.started_at
			this.extendOriginal = event.ended_at
		},

		cancelDrag() {
			if (this.createEvent && this.createEvent.id <= 0) {
				var idx = this.events.indexOf(this.createEvent)
				if (idx !== -1) this.events.splice(idx, 1)
			}
			this.createEvent = null
			this.createStart = null
			this.dragTime = null
			this.dragEvent = null
		},

		quickUpdateEvent(event) {
			var self = this
			var oldStart = this._dragOriginalStart
			var oldEnd = this._dragOriginalEnd
			this.axios.put('planners/' + event.id, {
				started_at: event.started_at,
				ended_at: event.ended_at,
				calendar_id: event.calendar_id,
				name: event.name,
				allday: event.allday || false,
			}).then(function() {
				// Server accepted: local state (this.events[i]) is already in sync thanks to
				// _syncEventToSource during mouseMove, so we do NOT re-fetch. A loadEvents() here
				// would momentarily blank out filteredCalendarEvents in week view and cause the
				// event to visibly flash back to its pre-drag position. Undo path still re-fetches
				// because that replaces state with server values.
				self.dragEvent = null
				if (oldStart && oldEnd) {
					self.undoSnackbar = {
						show: true, text: 'Evento spostato',
						undo: function() {
							self.axios.put('planners/' + event.id, {
								started_at: oldStart, ended_at: oldEnd,
								calendar_id: event.calendar_id, name: event.name,
								allday: false,
							}).then(function() { self.loadEvents() })
						}
					}
				}
			}).catch(function() {
				self.$store.commit('setSnackbar', { timeout: 3000, text: 'Errore aggiornamento evento', value: true, color: 'error' })
				self.dragEvent = null
				self.loadEvents()
			})
		},

		// --- Dialog ---
		openDialogFromTicket(ticket) {
			this.form = this.emptyForm()
			this.form.ticket = ticket
			this.form.name = '#' + ticket.number + (ticket.sectionalname ? '/' + ticket.sectionalname : '') + ' - ' + ticket.title
			var today = new Date()
			this.form.date = today.getFullYear() + '-' + String(today.getMonth()+1).padStart(2,'0') + '-' + String(today.getDate()).padStart(2,'0')
			var me = this.getCurrentUser()
			if (me) {
				this.form.users = [me]
				this.initCustomTimesForUsers([me])
			}
			this.dialogEdit = false
			this.dialogOpen = true
		},

		openDialogFromDrag(tempEvent) {
			// Remove temp event from array
			var idx = this.events.indexOf(tempEvent)
			if (idx !== -1) this.events.splice(idx, 1)
			this.createEvent = null

			var s = new Date(tempEvent.started_at)
			var e = new Date(tempEvent.ended_at)
			function p(n) { return n < 10 ? '0' + n : '' + n }

			this.form = this.emptyForm()
			this.form.date = s.getFullYear() + '-' + p(s.getMonth()+1) + '-' + p(s.getDate())
			this.form.startTime = p(s.getHours()) + ':' + p(s.getMinutes())
			this.form.endTime = p(e.getHours()) + ':' + p(e.getMinutes())
			var me = this.getCurrentUser()
			if (me) {
				this.form.users = [me]
				this.initCustomTimesForUsers([me])
			}
			this.dialogEdit = false
			this.dialogOpen = true
		},

		showEvent(ev) {
			if (ev.event && ev.event.id > 0) {
				var self = this
				this.dialogEdit = true
				var event = ev.event
				var s = new Date(event.started_at)
				var e = new Date(event.ended_at)
				function p(n) { return n < 10 ? '0' + n : '' + n }

				// Find full ticket from ticketsList by eventable_id
				var matchedTicket = null
				if (event.eventable_type === 'trouble' || event.eventable_type === 'helpdesktrouble') {
					for (var i = 0; i < this.ticketsList.length; i++) {
						if (this.ticketsList[i].id === event.eventable_id) {
							matchedTicket = this.ticketsList[i]
							break
						}
					}
					// Fallback if ticket not in list (e.g. filtered out by status exclusion)
					if (!matchedTicket && event.eventable_id) {
						matchedTicket = { id: event.eventable_id, title: event.entity_title || event.name, number: '' }
					}
				}

				this.form = {
					id: event.id,
					name: event.name || '',
					ticket: matchedTicket,
					date: s.getFullYear() + '-' + p(s.getMonth()+1) + '-' + p(s.getDate()),
					startTime: p(s.getHours()) + ':' + p(s.getMinutes()),
					endTime: p(e.getHours()) + ':' + p(e.getMinutes()),
					users: (event.users || []).map(function(u) {
						return { value: u.value, text: u.text, subtitle: u.subtitle }
					}),
					customTimes: {},
					vehicles: (event.vehicles || []).map(function(v) {
						return v.value ? v : Object.assign({}, v, { value: v.id, text: v.text || (v.alias + ' - ' + v.license_plate) })
					}),
					description: event.description || '',
				}
				// Popola customTimes per ogni utente: sempre pre-riempiti, flag touched se diversi dall'evento principale
				;(event.users || []).forEach(function(u) {
					var entry = { start: self.form.startTime, end: self.form.endTime, touched: false }
					if (u.started_at && u.ended_at) {
						var us = new Date(u.started_at)
						var ue = new Date(u.ended_at)
						entry.start = p(us.getHours()) + ':' + p(us.getMinutes())
						entry.end = p(ue.getHours()) + ':' + p(ue.getMinutes())
						if (u.started_at !== event.started_at || u.ended_at !== event.ended_at) {
							entry.touched = true
						}
					}
					self.$set(self.form.customTimes, u.value, entry)
				})
				this.dialogOpen = true
			}
			if (ev.nativeEvent) ev.nativeEvent.stopPropagation()
		},

		closeDialog() {
			this.dialogOpen = false
			this.form = this.emptyForm()
			this.confirmDelete = false
			if (this.confirmDeleteTimer) {
				clearTimeout(this.confirmDeleteTimer)
				this.confirmDeleteTimer = null
			}
			// Reset validation state so the next open starts clean (no red errors from previous open).
			if (this.$refs.form) this.$refs.form.resetValidation()
		},

		deleteEvent() {
			if (!this.form.id) return
			var self = this
			// First click: arm the confirm state for 4 seconds, then auto-revert.
			if (!this.confirmDelete) {
				this.confirmDelete = true
				if (this.confirmDeleteTimer) clearTimeout(this.confirmDeleteTimer)
				this.confirmDeleteTimer = setTimeout(function() {
					self.confirmDelete = false
					self.confirmDeleteTimer = null
				}, 4000)
				return
			}
			// Second click within 4s: actually delete.
			if (this.confirmDeleteTimer) {
				clearTimeout(this.confirmDeleteTimer)
				this.confirmDeleteTimer = null
			}
			this.deleting = true
			this.axios.post('planners/delete', { ids: [this.form.id] }).then(function() {
				self.deleting = false
				self.closeDialog()
				self.loadEvents()
				self.$store.commit('setSnackbar', { timeout: 3000, text: 'Evento eliminato', value: true, color: 'success' })
			}).catch(function() {
				self.deleting = false
				self.confirmDelete = false
				self.$store.commit('setSnackbar', { timeout: 3000, text: 'Errore eliminazione evento', value: true, color: 'error' })
			})
		},

		saveEvent() {
			// Inline validation via v-form rules — replaces the legacy snackbar fallback.
			if (this.$refs.form && !this.$refs.form.validate()) return
			this.saving = true

			// Build timestamps in ms
			var startMs = new Date(this.form.date + 'T' + this.form.startTime).getTime()
			var endMs = new Date(this.form.date + 'T' + this.form.endTime).getTime()

			// Build users payload from crewWithTimes (computed) — already applies touched/fallback logic.
			// Always sends started_at/ended_at so the backend can align personal calendar copies.
			var self = this
			var usersPayload = this.crewWithTimes.map(function(u) {
				return {
					value: u.value,
					text: u.text,
					started_at: new Date(self.form.date + 'T' + u.start).getTime(),
					ended_at: new Date(self.form.date + 'T' + u.end).getTime(),
				}
			})

			// Use the creator's PRIMARY calendar (Calendar.primary = true). Falls back to the
			// first available only as a safety net — backend filters team users by `whereHas('calendars')`,
			// so a creator without calendars wouldn't reach this point in practice.
			var primaryCalendar = (this.calendars || []).find(function(c) { return c.primary }) || (this.calendars && this.calendars[0])

			var payload = {
				name: this.form.name,
				started_at: startMs,
				ended_at: endMs,
				event_type: 'planner',
				allday: false,
				calendar_id: primaryCalendar ? primaryCalendar.id : null,
				description: this.form.description || '',
				users: usersPayload,
				vehicles: this.form.vehicles || [],
			}

			if (this.form.ticket && this.form.ticket.id) {
				payload.eventable_type = 'trouble'
				payload.eventable_id = this.form.ticket.id
			}

			var self = this
			if (this.dialogEdit && this.form.id) {
				this.axios.put('planners/' + this.form.id, payload).then(function(res) {
					self.saving = false
					self.closeDialog()
					self.loadEvents()
					self.$store.commit('setSnackbar', { timeout: 3000, text: 'Evento aggiornato', value: true, color: 'success' })
				}).catch(function() { self.saving = false })
			} else {
				this.axios.post('planners', payload).then(function(res) {
					self.saving = false
					self.closeDialog()
					self.loadEvents()
					self.$store.commit('setSnackbar', { timeout: 3000, text: 'Evento creato', value: true, color: 'success' })
				}).catch(function() { self.saving = false })
			}
		},

		onTicketSelected(ticket) {
			if (!ticket) {
				// Ticket cleared
				return
			}
			this.form.name = '#' + ticket.number + (ticket.sectionalname ? '/' + ticket.sectionalname : '') + ' - ' + ticket.title
		},

		// --- Helpers ---
		eventDurationMinutes(event) {
			return (event.ended_at - event.started_at) / (1000 * 60)
		},
		eventSizeClass(event) {
			var duration = this.eventDurationMinutes(event)
			if (duration >= 180) return 'planner-size-xl'
			if (duration >= 90) return 'planner-size-lg'
			if (duration >= 45) return 'planner-size-md'
			return 'planner-size-sm'
		},
		// Details (crew/customer/vehicles) shown for events ≥45min, hidden on tiny slots to avoid clutter.
		showEventDetails(event) {
			return this.eventDurationMinutes(event) >= 45
		},
		isPastEvent(event) {
			return new Date(event.ended_at) < new Date()
		},
		userHasCustomTime(event, user) {
			if (!user.started_at || !user.ended_at) return false
			return user.started_at !== event.started_at || user.ended_at !== event.ended_at
		},
		formatUserTime(user) {
			return this.formatTimeMs(user.started_at) + '-' + this.formatTimeMs(user.ended_at)
		},
		// Returns the background color v-calendar paints on the event card.
		getEventColor(event) {
			return event.color || '#546E7A'
		},
		// Pick black or white text based on background luminance. !important needed to beat
		// the default .v-event-timed cascade in Vuetify that would otherwise force light grey text.
		getEventTextColor(event) {
			var color = this.getEventColor(event)
			return this.$eventBusUtility.lightOrDark(color) === 'light'
				? 'color: #000 !important'
				: 'color: #fff !important'
		},
		toggleUserFilter(val) {
			var idx = this.selectedUsers.indexOf(val)
			if (idx === -1) this.selectedUsers.push(val)
			else this.selectedUsers.splice(idx, 1)
		},
		toggleVehicleFilter(val) {
			var idx = this.selectedVehicles.indexOf(val)
			if (idx === -1) this.selectedVehicles.push(val)
			else this.selectedVehicles.splice(idx, 1)
		},
		clearAllFilters() {
			this.selectedUsers = []
			this.selectedVehicles = []
		},
		toggleStatusFilter(status) {
			var idx = this.excludedStatuses.indexOf(status)
			if (idx === -1) this.excludedStatuses.push(status)
			else this.excludedStatuses.splice(idx, 1)
			// 'Chiuso' è mappato sul subFilter server not_closed → serve il reload.
			if (status === 'Chiuso') this.loadTickets()
		},
		ticketStatusFilterColor(status) {
			// Darker solid colors for filter chips (white text on these)
			if (!status) return 'grey'
			var s = status.toLowerCase()
			if (s === 'aperto') return 'blue'
			if (s === 'in corso') return 'orange darken-1'
			if (s === 'assegnato') return 'purple'
			if (s === 'chiuso') return 'grey'
			if (s === 'sospeso') return 'red'
			return 'grey'
		},
		ticketStatusColor(status) {
			// Lighter colors for sidebar status badges
			if (!status) return 'grey lighten-2'
			var s = status.toLowerCase()
			if (s === 'aperto') return 'blue lighten-4'
			if (s === 'in corso') return 'orange lighten-4'
			if (s === 'assegnato') return 'purple lighten-4'
			if (s === 'chiuso') return 'grey lighten-3'
			if (s === 'sospeso') return 'red lighten-4'
			return 'grey lighten-2'
		},
		ticketStatusTextColor(status) {
			if (!status) return 'grey'
			var s = status.toLowerCase()
			if (s === 'aperto') return 'blue darken-2'
			if (s === 'in corso') return 'orange darken-3'
			if (s === 'assegnato') return 'purple darken-2'
			if (s === 'chiuso') return 'grey darken-1'
			if (s === 'sospeso') return 'red darken-2'
			return 'grey'
		},
		priorityColor(priority) {
			if (priority === '1' || priority === 1) return '#F44336'
			if (priority === '2' || priority === 2) return '#FF9800'
			if (priority === '3' || priority === 3) return '#4CAF50'
			return '#9E9E9E'
		},
		ticketPriorityColor(t) {
			var p = t.trouble_priority
			if (p === '1' || p === 1) return 'red'
			if (p === '2' || p === 2) return 'orange'
			if (p === '3' || p === 3) return 'blue'
			return 'grey'
		},
		isExpiringSoon(v) {
			if (!v.next_expiration) return false
			var exp = new Date(v.next_expiration)
			var now = new Date()
			var diff = (exp - now) / (1000 * 60 * 60 * 24)
			return diff <= 30
		},
		formatTimeMs(ms) {
			var d = new Date(ms)
			var h = d.getHours()
			var m = d.getMinutes()
			return (h < 10 ? '0' + h : h) + ':' + (m < 10 ? '0' + m : m)
		},
		showEventFromAgenda(event) {
			this.showEvent({ event: event, nativeEvent: null })
		},
		isAgendaExpanded(id) {
			return this.expandedAgendaIds.indexOf(id) !== -1
		},
		toggleAgendaExpand(id) {
			var idx = this.expandedAgendaIds.indexOf(id)
			if (idx === -1) this.expandedAgendaIds.push(id)
			else this.expandedAgendaIds.splice(idx, 1)
		},
		expandAllAgenda() {
			this.expandedAgendaIds = this.agendaEvents.map(function(e) { return e.id })
		},
		collapseAllAgenda() {
			this.expandedAgendaIds = []
		},
		vehicleExpirationColor(v) {
			if (!v.next_expiration) return 'grey'
			var exp = new Date(v.next_expiration)
			var now = new Date()
			var diff = (exp - now) / (1000 * 60 * 60 * 24)
			if (diff < 0) return 'red'
			if (diff <= 30) return 'orange'
			return 'green'
		},
	},

}
</script>

<style scoped>
/* Event card — aligned with YelloCalendar.vue pattern (.v-event-draggable / .event-title / .event-schedule).
   The wrapper carries `:style="getEventTextColor(event)"` — propagate that color to ALL descendants
   (Vuetify's `.v-event-timed` would otherwise force a default light-grey on inline content). */
.v-event-draggable,
.v-event-draggable * {
	color: inherit !important;
}
.v-event-draggable {
	padding-left: 6px;
	padding-right: 4px;
	height: 100%;
	overflow: hidden;
	position: relative;
}
/* Crew is the primary info in the planner — render with stronger weight than the title. */
.event-crew {
	font-weight: 700;
	line-height: 1.3;
	margin-bottom: 1px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.crew-custom-time { font-weight: 400; opacity: 0.85; }
.event-title {
	white-space: initial;
	font-weight: 500;
	line-height: 1.25;
	opacity: 0.95;
}
.event-schedule {
	white-space: initial;
	line-height: 1.2;
	opacity: 0.85;
}
.past-event { opacity: 0.5; }
.v-event-draggable--dragging { opacity: 0.6; }

/* Secondary sub-blocks: customer, vehicles. Shown only for events ≥45min (v-if showEventDetails). */
.event-customer,
.event-vehicles {
	line-height: 1.3;
	margin-top: 2px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	opacity: 0.9;
}

/* Duration-based size scaling (kept to decide font size + whether secondary info shows). */
.planner-size-sm .event-title { font-size: 11px; }
.planner-size-sm .event-schedule { font-size: 10px; }

.planner-size-md .event-title { font-size: 12px; }
.planner-size-md .event-schedule { font-size: 11px; }
.planner-size-md .event-crew,
.planner-size-md .event-customer,
.planner-size-md .event-vehicles { font-size: 11px; }

.planner-size-lg .event-title { font-size: 14px; }
.planner-size-lg .event-schedule { font-size: 12px; }
.planner-size-lg .event-crew,
.planner-size-lg .event-customer,
.planner-size-lg .event-vehicles { font-size: 12px; margin-top: 3px; }

.planner-size-xl .event-title { font-size: 15px; line-height: 1.4; }
.planner-size-xl .event-schedule { font-size: 13px; }
.planner-size-xl .event-crew,
.planner-size-xl .event-customer,
.planner-size-xl .event-vehicles { font-size: 13px; margin-top: 4px; }

/* Day view is roomier — allow wrapping for sub-blocks */
.planner-view-day .event-crew,
.planner-view-day .event-customer,
.planner-view-day .event-vehicles { white-space: normal; }

/* Week view: narrow columns → show only crew + title + schedule. Customer and vehicles hidden
   (available in Day view, Agenda view, and on click). Crew remains visible because it is the
   primary piece of info in the planner. */
.planner-view-week .event-customer,
.planner-view-week .event-vehicles { display: none; }
/* Ensure crew + title + schedule remain ellipsis-truncated in the tight week columns.
   `!important` is required to override YelloCalendar.vue's GLOBAL (non-scoped) rule
   `.event-title { white-space: initial !important }` which would otherwise force wrap. */
.planner-view-week .event-crew,
.planner-view-week .event-title,
.planner-view-week .event-schedule {
	white-space: nowrap !important;
	overflow: hidden;
	text-overflow: ellipsis;
}

.v-event-drag-bottom {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 4px;
	height: 4px;
	cursor: ns-resize;
}
.v-event-drag-bottom::after {
	display: none;
	position: absolute;
	left: 50%;
	height: 4px;
	border-top: 1px solid white;
	border-bottom: 1px solid white;
	width: 16px;
	margin-left: -8px;
	opacity: 0.8;
	content: '';
}
.v-event-drag-bottom:hover::after { display: block; }
/* NB: le regole per .planner-title-input sono in un blocco <style> globale
   a fine file, perché il v-dialog viene teleportato fuori dal DOM del componente
   e le regole scoped (via data-v-…) non lo raggiungono. */
.planner-search ::v-deep .v-input__control {
	min-height: 32px;
}
.planner-search ::v-deep input {
	font-size: 12px;
	padding: 4px 0;
}
.planner-search ::v-deep .v-input__append-inner {
	margin-top: 4px !important;
}
.planner-ticket-item {
	cursor: pointer;
}
.planner-ticket-item:hover {
	background: rgba(0, 0, 0, 0.04);
}
.theme--dark .planner-ticket-item:hover {
	background: rgba(255, 255, 255, 0.06);
}
/* Agenda view — collapsible rows. Each row carries a coloured left border driven by priority
   (same palette as getEventColor) so the priority is visible even when collapsed. */
.planner-agenda-item {
	border-left: 4px solid #546E7A;
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
	border-radius: 2px;
	margin-bottom: 2px;
	transition: background 0.15s;
}
.theme--dark .planner-agenda-item {
	border-bottom-color: rgba(255, 255, 255, 0.08);
}
.planner-agenda-item.expanded {
	background: rgba(0, 0, 0, 0.03);
}
.theme--dark .planner-agenda-item.expanded {
	background: rgba(255, 255, 255, 0.04);
}
.planner-agenda-header {
	padding: 10px 12px;
	cursor: pointer;
}
.planner-agenda-header:hover {
	background: rgba(0, 0, 0, 0.04);
}
.theme--dark .planner-agenda-header:hover {
	background: rgba(255, 255, 255, 0.06);
}
.planner-agenda-details {
	padding: 8px 16px 12px 36px;
	font-size: 13px;
	border-top: 1px dashed rgba(0, 0, 0, 0.12);
	background: inherit;
}
.theme--dark .planner-agenda-details {
	border-top-color: rgba(255, 255, 255, 0.12);
}
.planner-agenda-time {
	font-size: 13px;
	font-weight: 500;
	color: rgba(0, 0, 0, 0.7);
	min-width: 120px;
}
.theme--dark .planner-agenda-time { color: rgba(255, 255, 255, 0.8); }
.planner-ticket-item.dragging {
	opacity: 0.5;
	background: rgba(25, 118, 210, 0.08);
}

/* --- Layout wrapper: round-card + toolbar + sidebar scroll ---
   Colori theme-aware: il wrapper .planner-card usa la classe Vuetify `background-2`
   (plugins/vuetify.js: light #FFFFFF, dark #424242). I bordi e sfondi hardcoded
   hanno override `.theme--dark` qui sotto per il dark mode. */
.planner-row { height: calc(100vh - 204px); }
.planner-sidebar { height: 100%; }
.planner-sidebar-left { border-right: 1px solid rgba(0, 0, 0, 0.12); }
.planner-sidebar-right { border-left: 1px solid rgba(0, 0, 0, 0.12); }
.theme--dark .planner-sidebar-left { border-right-color: rgba(255, 255, 255, 0.12); }
.theme--dark .planner-sidebar-right { border-left-color: rgba(255, 255, 255, 0.12); }
/* min-height:0 è richiesto perché flex-grow:1 con overflow-y-auto scrolli correttamente */
.planner-sidebar-scroll { min-height: 0; }
/* Sezioni della sidebar destra (operatori + veicoli) — header/search fissi, lista scrollabile */
.planner-side-section { flex: 1 1 0; min-height: 0; }
.planner-side-scroll { min-height: 0; }
.planner-calendar-col { height: 100%; overflow: hidden; }

/* Highlight riga filtro attiva: background tinto primario + testo bold */
.planner-filter-item.planner-filter-active {
	background: rgba(25, 118, 210, 0.08);
}
.theme--dark .planner-filter-item.planner-filter-active {
	background: rgba(100, 181, 246, 0.16);
}
.planner-filter-item.planner-filter-active ::v-deep .v-list-item__title {
	font-weight: 600;
	color: rgba(0, 0, 0, 0.87);
}
.theme--dark .planner-filter-item.planner-filter-active ::v-deep .v-list-item__title {
	color: rgba(255, 255, 255, 0.95);
}
/* Allinea verticalmente checkbox + avatar + testo (default v-list-item-action ha align-self: flex-start) */
.planner-filter-item ::v-deep .v-list-item__action {
	align-self: center;
	min-width: 24px;
	margin: 0 6px 0 0 !important;
	display: flex;
	align-items: center;
	justify-content: center;
}
.planner-filter-item ::v-deep .v-list-item__action .v-simple-checkbox {
	display: flex;
	align-items: center;
	justify-content: center;
	line-height: 1;
	height: 24px;
}
.planner-filter-item ::v-deep .v-list-item__avatar {
	margin-top: 0;
	margin-bottom: 0;
	margin-right: 4px;
	align-self: center;
}

/* Toolbar agenda (espandi/collassa tutti) — theme-aware */
.planner-agenda-toolbar {
	background: inherit;
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
	position: sticky;
	top: 0;
	z-index: 1;
}
.theme--dark .planner-agenda-toolbar { border-bottom-color: rgba(255, 255, 255, 0.12); }

/* Titolo data del picker in capitalize: "aprile 2026" -> "Aprile 2026" + bold */
.planner-title { text-transform: capitalize; font-weight: 700; }

/* Input search outlined dense compatti */
.planner-search.v-text-field--outlined ::v-deep .v-input__slot { min-height: 36px; }
.planner-search.v-text-field--outlined ::v-deep fieldset { border-color: rgba(0,0,0,0.12); }
.theme--dark .planner-search.v-text-field--outlined ::v-deep fieldset { border-color: rgba(255,255,255,0.24); }
.planner-search.v-text-field--outlined ::v-deep .v-label { top: 8px; }
.planner-search.v-text-field--outlined ::v-deep .v-input__append-inner { margin-top: 6px !important; }
</style>

<!-- Blocco globale: regole che devono funzionare dentro il v-dialog (teleportato fuori dal DOM
     del componente, quindi irraggiungibile dal <style scoped> via attributo data-v-…). -->
<style>
.planner-title-input .v-input__slot {
	padding: 0 !important;
	min-height: unset !important;
	background: transparent !important;
	box-shadow: none !important;
}
</style>
